import { DemoResetCard } from '@/components/demo/DemoResetCard';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import { getDemoStatus, isDemoModeEnabled } from '@/lib/demo';
import { useAuthStore } from '@/stores/auth-store';
import { useQuery } from '@tanstack/react-query';
import { Link, createFileRoute, notFound, redirect } from '@tanstack/react-router';
import { BookOpen, Printer, Users } from 'lucide-react';
import { toast } from 'sonner';

export const Route = createFileRoute('/_app/admin/demo')({
  beforeLoad: () => {
    if (!isDemoModeEnabled()) {
      throw notFound();
    }
    const { user } = useAuthStore.getState();
    if (user?.role !== 'ORGANIZER') {
      toast.info('Solo los organizadores pueden gestionar el modo demo.');
      throw redirect({ to: '/' });
    }
  },
  component: DemoAdminPage,
});

function DemoAdminPage() {
  const { data, isLoading, isError } = useQuery({
    queryKey: ['demo', 'status'],
    queryFn: getDemoStatus,
    staleTime: 10_000,
  });

  return (
    <main className="flex-1 p-6 space-y-6">
      <div>
        <h1 className="text-2xl font-semibold tracking-tight">Modo demo</h1>
        <p className="text-muted-foreground mt-1 text-sm">
          Estado actual del ambiente demo y herramientas de mantenimiento.
        </p>
      </div>

      <Card>
        <CardHeader>
          <CardTitle>Estado</CardTitle>
          <CardDescription>Resumen del evento demo activo y conteos por tipo.</CardDescription>
        </CardHeader>
        <CardContent className="space-y-3">
          {isLoading ? (
            <div className="space-y-2">
              <Skeleton className="h-4 w-48" />
              <Skeleton className="h-4 w-64" />
              <Skeleton className="h-4 w-40" />
            </div>
          ) : isError || !data ? (
            <p className="text-destructive text-sm">No se pudo cargar el estado del demo.</p>
          ) : (
            <div className="space-y-2 text-sm">
              <div className="flex items-center gap-2">
                <span className="text-muted-foreground">Habilitado:</span>
                <Badge variant={data.enabled ? 'default' : 'secondary'}>
                  {data.enabled ? 'sí' : 'no'}
                </Badge>
              </div>
              <div>
                <span className="text-muted-foreground">Preset:</span>{' '}
                <span className="font-mono">{data.preset ?? '—'}</span>
              </div>
              <div>
                <span className="text-muted-foreground">Event ID:</span>{' '}
                <span className="font-mono text-xs">{data.eventId ?? '—'}</span>
              </div>
              <div>
                <span className="text-muted-foreground">Último reset:</span>{' '}
                <span>
                  {data.lastResetAt ? new Date(data.lastResetAt).toLocaleString() : 'nunca'}
                </span>
              </div>
              <div className="grid grid-cols-2 gap-2 pt-2 sm:grid-cols-5">
                <CountTile label="Seed" value={data.counts.seedAttendees} />
                <CountTile label="Actores" value={data.counts.actorAttendees} />
                <CountTile label="Talks" value={data.counts.talks} />
                <CountTile label="Sponsors" value={data.counts.sponsors} />
                <CountTile label="Scans" value={data.counts.scans} />
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Galería de QRs</CardTitle>
          <CardDescription>Vista imprimible de todos los QRs del evento demo.</CardDescription>
        </CardHeader>
        <CardContent>
          <Button asChild variant="outline">
            <Link to="/admin/demo/qr-gallery">
              <Printer className="mr-2 h-4 w-4" />
              Abrir galería
            </Link>
          </Button>
        </CardContent>
      </Card>

      <DemoResetCard />

      <Card>
        <CardHeader>
          <CardTitle>
            <BookOpen className="inline-block mr-2 h-5 w-5 align-text-bottom" />
            Cómo correr una demo en vivo
          </CardTitle>
          <CardDescription>Pasos para mostrar el flujo completo a un prospecto.</CardDescription>
        </CardHeader>
        <CardContent>
          <ol className="list-decimal list-inside space-y-2 text-sm">
            <li>Resetea el demo si es la primera demo del día (opcional, no obligatorio).</li>
            <li>
              Comparte el link{' '}
              <Link to="/demo/enroll" className="underline underline-offset-2 font-medium">
                /demo/enroll
              </Link>{' '}
              con el prospecto para que se inscriba.
            </li>
            <li>
              Abre la galería de QRs para confirmar que el prospecto aparece arriba; simula un
              escaneo de asistencia con el lector.
            </li>
            <li>Envía el certificado por email desde la lista de inscritos del evento demo.</li>
          </ol>
        </CardContent>
      </Card>

      <div className="grid gap-4 sm:grid-cols-3">
        <ShortcutCard
          title="Ver inscritos del evento demo"
          description="Lista de asistentes filtrada por el evento demo activo."
          icon={<Users className="h-5 w-5" />}
          href={
            data?.eventId
              ? { to: '/attendees', search: { page: 1, pageSize: 10, eventId: data.eventId } }
              : null
          }
        />
        <ShortcutCard
          title="Abrir pasaporte demo"
          description="Vista general del pasaporte; filtra por evento demo."
          icon={<BookOpen className="h-5 w-5" />}
          href={{ to: '/admin/passport' }}
        />
        <ShortcutCard
          title="Enviar certificados al evento demo"
          description="Abre la lista de inscritos para lanzar el broadcast de certificados."
          icon={<Printer className="h-5 w-5" />}
          href={
            data?.eventId
              ? { to: '/attendees', search: { page: 1, pageSize: 10, eventId: data.eventId } }
              : null
          }
        />
      </div>
    </main>
  );
}

function CountTile({ label, value }: { label: string; value: number }) {
  return (
    <div className="rounded-md border p-2 text-center">
      <div className="text-xs text-muted-foreground">{label}</div>
      <div className="text-lg font-semibold tabular-nums">{value}</div>
    </div>
  );
}

type ShortcutHref =
  | { to: '/attendees'; search: { page: number; pageSize: number; eventId: string } }
  | { to: '/admin/passport' }
  | null;

function ShortcutCard({
  title,
  description,
  icon,
  href,
}: {
  title: string;
  description: string;
  icon: React.ReactNode;
  href: ShortcutHref;
}) {
  const disabled = href === null;

  return (
    <Card className={disabled ? 'opacity-50' : undefined}>
      <CardHeader className="pb-2">
        <CardTitle className="text-base flex items-center gap-2">
          {icon}
          {title}
        </CardTitle>
        <CardDescription className="text-xs">{description}</CardDescription>
      </CardHeader>
      <CardContent>
        {disabled ? (
          <Button variant="outline" size="sm" disabled>
            Ir
          </Button>
        ) : (
          <Button asChild variant="outline" size="sm">
            <Link {...href}>Ir</Link>
          </Button>
        )}
      </CardContent>
    </Card>
  );
}
